import { ChangeEvent, useEffect, useState } from 'react'
import { add, differenceInDays, isBefore, isSameDay, isValid, parse, sub } from 'date-fns'

import {
  ActionButton,
  Calendar,
  CalendarProps,
  Dropdown,
  Flex,
  HStack,
  Icon,
  Input,
  Tag,
  Text,
  VStack,
} from '@revolut-internal/ui-kit'
import { CalendarDateProps } from '@revolut-internal/ui-kit/types/components/Calendar/CalendarDate'

import { useToasts } from 'modules/toasts/ToastsProvider'
import { FCWithChildren, TimeRange } from 'types'
import { convertDateToTimeRangeFormat } from 'utils/time/convertDateToTimeRangeFormat'
import { convertTimeRangeToDateFormat } from 'utils/time/convertTimeRangeToDateFormat'
import { extendRangeToUtcDayLimits } from 'utils/time/extendRangeToUtcDayLimits'
import { getTimeStringFromDate } from 'utils/time/getTimeStringFromDate'

import { useTimeRange } from './useTimeRange'

export type DatePreset = {
  key: string
  label: string
  getValue: () => { from: Date; to: Date }
}
export type RangeFilterValue = {
  timeRange: TimeRange
  start?: string
  end?: string
}

type RangeFilterContentProps = Omit<
  CalendarProps,
  'value' | 'defaultValue' | 'onChange' | 'variant'
> &
  Pick<CalendarDateProps, 'disabledDays'> & {
    labelEmptyValue?: string
    timeRange?: RangeFilterValue
    supportsOnlyAdjacentDates?: boolean
    supportsTime?: boolean
    timeLabel?: string
    presets?: DatePreset[]
    shouldValidateTime?: boolean
    onSave: (value: RangeFilterValue | null) => void
  }

const getAdjacentDays = (timeRange: TimeRange<Date>, nextRange: TimeRange<Date>) =>
  isSameDay(nextRange.from, timeRange.from)
    ? {
        from: nextRange.from,
        to: add(nextRange.from, { days: 1 }),
      }
    : {
        from: sub(nextRange.to, { days: 1 }),
        to: nextRange.to,
      }

const isTimeConsecutiveValid = (
  start: { date?: Date; time?: string },
  end: { date?: Date; time?: string },
) => {
  const { date: startDate, time: startTimeStr } = start
  const { date: endDate, time: endTimeStr } = end

  if (!startDate || !startTimeStr || !endDate || !endTimeStr) {
    return true
  }

  const startTime = parse(startTimeStr, 'HH:mm', startDate)
  const endTime = parse(endTimeStr, 'HH:mm', endDate)

  const areTimesValid = isValid(startTime) && isValid(endTime)

  if (!areTimesValid) {
    return true
  }

  return isBefore(startTime, endTime)
}

export const RangeFilterContent: FCWithChildren<RangeFilterContentProps> = ({
  timeRange: initialTimeRange,
  supportsOnlyAdjacentDates = false,
  supportsTime = false,
  timeLabel = 'Time range',
  presets = [],
  disabledDays,
  shouldValidateTime = true,
  onSave,
  children,
  ...calendarProps
}) => {
  const { addToast } = useToasts()

  const { timeRange, setTimeRange } = useTimeRange(initialTimeRange?.timeRange)
  const [startTime, setStartTime] = useState(initialTimeRange?.start || '')
  const [endTime, setEndTime] = useState(initialTimeRange?.end || '')

  const [timeError, setTimeError] = useState({
    state: false,
    isTouched: false,
  })

  const handlePresetClick = (preset: DatePreset) => {
    const { from, to } = preset.getValue()

    setTimeError({ state: false, isTouched: true })

    setStartTime(getTimeStringFromDate(from))
    setEndTime(getTimeStringFromDate(to))
    setTimeRange({ from, to })
  }

  const handleSave = () => {
    if (
      shouldValidateTime &&
      supportsTime &&
      !isTimeConsecutiveValid(
        { date: timeRange?.from, time: startTime },
        { date: timeRange?.to, time: endTime },
      )
    ) {
      setTimeError({
        state: true,
        isTouched: true,
      })
      return
    }

    timeRange &&
      onSave({
        timeRange: convertTimeRangeToDateFormat(timeRange),
        start: startTime,
        end: endTime,
      })
  }

  const handleClear = () => {
    setStartTime('')
    setEndTime('')
    onSave(null)
  }

  const handleChange = (nextRange: TimeRange<Date> | null) => {
    if (!nextRange) {
      return
    }

    const shouldMakeAdjacent =
      supportsOnlyAdjacentDates && differenceInDays(nextRange?.from, nextRange?.to) < -1

    setTimeRange(
      convertDateToTimeRangeFormat(
        extendRangeToUtcDayLimits(
          shouldMakeAdjacent && timeRange
            ? getAdjacentDays(timeRange, nextRange)
            : nextRange,
        ),
      ),
    )

    if (supportsOnlyAdjacentDates && shouldMakeAdjacent) {
      addToast('The maximum range is 2 days')
    }
  }

  useEffect(() => {
    if (!shouldValidateTime || !timeError.isTouched) {
      return
    }

    setTimeError({
      state: !isTimeConsecutiveValid(
        { date: timeRange?.from, time: startTime },
        { date: timeRange?.to, time: endTime },
      ),
      isTouched: true,
    })
  }, [
    timeRange?.from,
    timeRange?.to,
    startTime,
    endTime,
    timeError.isTouched,
    shouldValidateTime,
  ])

  return (
    <>
      <Calendar
        {...calendarProps}
        variant="range"
        value={timeRange}
        disabledDays={
          supportsOnlyAdjacentDates
            ? {
                before: sub(new Date(), { weeks: 1 }),
                after: add(new Date(), { weeks: 1 }),
              }
            : disabledDays
        }
        onChange={handleChange}
      />

      {Boolean(presets.length) && (
        <HStack mt="s-16" px="s-16" space="s-8">
          {presets.map(preset => (
            <Tag
              key={preset.key}
              use="button"
              variant="status"
              onClick={() => handlePresetClick(preset)}
            >
              {preset.label}
            </Tag>
          ))}
        </HStack>
      )}

      {supportsTime && (
        <VStack aria-label={timeLabel}>
          <VStack p="s-16" gap="s-8">
            <Text variant="emphasis2">{timeLabel}:</Text>
            <Flex gap="s-8">
              <Input
                type="time"
                label="Start"
                value={startTime}
                invalid={Boolean(timeError.state)}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setStartTime(event.target.value)
                }
              />
              <Input
                type="time"
                label="End"
                value={endTime}
                invalid={Boolean(timeError.state)}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setEndTime(event.target.value)
                }
              />
            </Flex>
            {timeError.state && (
              <HStack
                space="s-8"
                py="s-2"
                px="s-8"
                color="red"
                align="center"
                style={{
                  whiteSpace: 'pre-wrap',
                }}
              >
                <Icon name="ExclamationMarkOutline" size={12} />
                <Text flex="1" variant="small">
                  Start time should be less than end time
                </Text>
              </HStack>
            )}
          </VStack>
        </VStack>
      )}

      {children}

      <Dropdown.Group
        sticky="bottom"
        style={!supportsTime ? { boxShadow: 'none' } : undefined}
      >
        <HStack p="s-16" space="s-16">
          <ActionButton width="100%" onClick={handleClear}>
            Clear
          </ActionButton>
          <ActionButton variant="accent" width="100%" onClick={handleSave}>
            Apply
          </ActionButton>
        </HStack>
      </Dropdown.Group>
    </>
  )
}
